/* eslint-disable no-restricted-imports */
import { Elements } from '@stripe/react-stripe-js';
import { Checkbox as AntCheckbox } from 'antd';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useIsMutating } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  getValidateAddressInformationMutationKey,
  type LiteQuote,
  useBillingAddress,
  useFacility,
} from '@frontend/api';
import { Button, type ButtonProps } from '@frontend/components';

import AddressValidationModal from 'components/AddressValidationModal/AddressValidationModal';

import { PAYMENT_METHOD } from 'constants/all';

import BillingAddressDrawer from 'modules/common/drawers/billingAddressDrawer/BillingAddressDrawer';
import { open as openBillingAddressDrawer } from 'modules/common/drawers/billingAddressDrawer/data/actions';
import { MODES as BILLING_ADDRESS_DRAWER_MODES } from 'modules/common/drawers/billingAddressDrawer/data/constants';
import FacilityDrawer from 'modules/common/drawers/facilityDrawer/FacilityDrawer';
import { open as openFacilityDrawer } from 'modules/common/drawers/facilityDrawer/data/actions';
import { MODES as FACILITY_DRAWER_MODES } from 'modules/common/drawers/facilityDrawer/data/constants';
import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';
import SupplierFacilitySelector from 'modules/common/smartComponents/supplierFacilitySelector/SupplierFacilitySelector';

import { getIsQuoteReadOnly } from 'store/quotes/selectors';
import { useFeatureFlagValue } from 'store/session/hooks';

import { stripePromise } from 'utils/stripePromise';

import {
  addressValidationModal,
  billingAddressForm,
  selectNewlyAddedBillingAddress,
  selectNewlyAddedFacility,
  setBillingAddress,
  setCustomerInfoStep,
  setFacility,
  setSupplierFacilityId,
  setUseShippingAddress,
  shippingAddressForm,
} from '../../data/actions';
import { useConfirmAddress, useEditAddress } from '../../data/hooks';
import {
  getAccount,
  getBillingAddress,
  getBillingAddresses,
  getBillingInformation,
  getBillingInformationType,
  getCard,
  getContact,
  getCustomerInfoStep,
  getFacilities,
  getFacility,
  getOriginalAddress,
  getPaymentMethod,
  getPurchaseOrderNumber,
  getShippingInformation,
  getShippingInformationType,
  getShippingType,
  getSuggestedAddresses,
  getSupplierFacilityId,
  getSupplierFacilities,
  getVisitedCustomerInformationSteps,
  getHasValidShipping,
  getIsAddressValidationModalOpen,
  getIsFetchingShippingRates,
  getIsShowingBillingAddressForm,
  getIsShowingShippingAddressForm,
  getIsUsingShippingAddressForBilling,
  getIsValidatingCC,
} from '../../data/reducer';
import { convertContactToAddressInformation } from '../../data/utils';
import {
  BILLING_INFO_TYPES,
  CUSTOMER_INFO_STEPS,
  SHIPPING_INFO_TYPES,
} from '../constants';
import BillingAddressSelector from './BillingAddressSelector';
import BillingInformationDisplay from './BillingInformationDisplay';
import CustomerAddressForm from './CustomerAddressForm';
import FacilitySelector from './FacilitySelector';
import PaymentDetails from './PaymentDetails';
import PaymentMethodForm from './PaymentMethodForm';
import ShippingInformationDisplay from './ShippingInformationDisplay';
import ShippingOptionsDisplay from './ShippingOptionsDisplay';
import ShippingOptionsForm from './ShippingOptionsForm';

const AddAddressButton = styled.button`
  border: none;
  color: #06f;
  cursor: pointer;
  font-size: 12px;
  outline: none;
  padding-left: 5px;
  text-decoration: underline;
`;

const CancelButton = forwardRef<HTMLElement, ButtonProps>((props, ref) => {
  return <Button variant="secondary" marginRight={1.25} {...props} ref={ref} />;
});

CancelButton.displayName = 'CancelButton';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const EditButton = styled.button`
  font-size: 11px;
  color: #06f;
  cursor: pointer;
  margin-right: 17px;
  text-decoration: underline;
  ${(props) => props.hidden && 'display: none;'}
`;

const PaymentDetailsDisplay = styled.div`
  font-size: 13px;
`;

const Step = styled.div`
  border-left: 1px solid #ddd;
  border-right: 1px solid #ddd;
  border-top: 1px solid #ddd;
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
  width: 100%;
`;

const StepHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-top: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #ddd;
`;

const StepBody = styled.div`
  border-bottom: 1px solid #ddd;
  color: #5b5b5b;
  padding-bottom: 14px;
  padding-left: 22px;
  padding-right: 22px;
  padding-top: 14px;
  ${(props) => {
    if (props.hidden) return 'display: none; border-bottom: none;';
  }}
`;

const StepTitle = styled.span`
  font-size: 16px;
  font-weight: bold;
  margin-left: 17px;
`;

const StepFooter = styled.div`
  background-color: #eee;
  border-bottom: 1px solid #ddd;
  display: flex;
  flex-direction: row;
  justify-content: ${(props) =>
    props && props.rightAlignContent ? 'flex-end' : 'space-between'};
  padding: 9px;
`;

const NavigationButtons = styled.div`
  display: flex;
  flex-direction: row;
`;

const PaymentMethod = styled.span`
  font-size: 13px;
`;

const SupplierFacility = styled.span`
  font-size: 13px;
`;

type CustomerInformationProps = {
  quote: LiteQuote;
};

const CustomerInformation = ({ quote }: CustomerInformationProps) => {
  const dispatch = useDispatch();

  const closeAddressValidationModal = useCallback(
    () => dispatch(addressValidationModal.close()),
    [dispatch],
  );

  const account = useSelector(getAccount);

  const confirmAddress = useConfirmAddress();
  const editAddress = useEditAddress();

  const hideBillingAddressForm = useCallback(
    () => dispatch(billingAddressForm.hide()),
    [dispatch],
  );

  const hideShippingAddressForm = useCallback(
    () => dispatch(shippingAddressForm.hide()),
    [dispatch],
  );

  const openBillingAddress = useCallback(
    (data: any) => dispatch(openBillingAddressDrawer.request(data)),
    [dispatch],
  );

  const openFacility = useCallback(
    (data: any) => dispatch(openFacilityDrawer.request(data)),
    [dispatch],
  );

  const [newFacilityId, setNewFacilityId] = useState<string | null>(null);

  const { data: newlyAddedFacility } = useFacility(
    { facilityId: newFacilityId ?? '' },
    {
      queryOptions: {
        enabled: !!newFacilityId,
      },
      onError: () => dispatch(selectNewlyAddedFacility.error()),
    },
  );

  useEffect(() => {
    if (newlyAddedFacility) {
      dispatch(
        selectNewlyAddedFacility.success({
          facility: newlyAddedFacility,
        }),
      );
    }
  }, [newlyAddedFacility, dispatch]);

  const [newBillingAddressId, setNewBillingAddressId] = useState<string | null>(
    null,
  );

  const { data: newlyAddedBillingAddress } = useBillingAddress(
    { billingAddressId: newBillingAddressId ?? '' },
    {
      queryOptions: {
        enabled: !!newBillingAddressId,
      },
      onError: () => dispatch(selectNewlyAddedBillingAddress.error()),
    },
  );

  useEffect(() => {
    if (newlyAddedBillingAddress) {
      dispatch(
        selectNewlyAddedBillingAddress.success({
          billingAddress: newlyAddedBillingAddress,
        }),
      );
    }
  }, [newlyAddedBillingAddress, dispatch]);

  const selectNewBillingAddress = useCallback((billingAddressId: string) => {
    setNewBillingAddressId(billingAddressId);
  }, []);

  const selectNewFacility = useCallback((facilityId: string) => {
    setNewFacilityId(facilityId);
  }, []);

  const changeBillingAddress = useCallback(
    (data: any) => dispatch(setBillingAddress(data)),
    [dispatch],
  );

  const changeFacility = useCallback(
    (data: any) => dispatch(setFacility(data)),
    [dispatch],
  );

  const changeSupplierFacilityId = useCallback(
    (data: any) => dispatch(setSupplierFacilityId(data)),
    [dispatch],
  );

  const setStep = useCallback(
    (data: any) => dispatch(setCustomerInfoStep(data)),
    [dispatch],
  );

  const changeUseShippingAddress = useCallback(
    (data: any) => dispatch(setUseShippingAddress(data)),
    [dispatch],
  );

  const showBillingAddressForm = useCallback(
    () => dispatch(billingAddressForm.show()),
    [dispatch],
  );

  const showShippingAddressForm = useCallback(
    () => dispatch(shippingAddressForm.show()),
    [dispatch],
  );

  const billingAddress = useSelector(getBillingAddress);
  const billingAddresses = useSelector(getBillingAddresses);
  const billingInformation = useSelector(getBillingInformation);
  const billingInformationType = useSelector(getBillingInformationType);
  const contact = useSelector(getContact);
  const card = useSelector(getCard);
  const facility = useSelector(getFacility);
  const facilities = useSelector(getFacilities);
  const hasValidShipping = useSelector(getHasValidShipping);
  const isAddressValidating = Boolean(
    useIsMutating({
      mutationKey: getValidateAddressInformationMutationKey(),
    }),
  );
  const isAddressValidationModalOpen = useSelector(
    getIsAddressValidationModalOpen,
  );
  const isFetchingShippingRates = useSelector(getIsFetchingShippingRates);
  const isQuoteReadOnly = useSelector(getIsQuoteReadOnly);
  const isValidatingCC = useSelector(getIsValidatingCC);
  const originalAddress = useSelector(getOriginalAddress);
  const paymentMethod = useSelector(getPaymentMethod);
  const purchaseOrderNumber = useSelector(getPurchaseOrderNumber);
  const selectedStep = useSelector(getCustomerInfoStep);
  const shippingInformation = useSelector(getShippingInformation);
  const shippingInformationType = useSelector(getShippingInformationType);
  const shippingType = useSelector(getShippingType);
  const supplierFacilityId = useSelector(getSupplierFacilityId);
  const supplierFacilities = useSelector(getSupplierFacilities);
  const isShowingBillingAddressForm = useSelector(
    getIsShowingBillingAddressForm,
  );
  const isShowingShippingAddressForm = useSelector(
    getIsShowingShippingAddressForm,
  );
  const visitedSteps = useSelector(getVisitedCustomerInformationSteps);
  const maxSupplierFacilities = useFeatureFlagValue(
    featureFlagKeys.maxSupplierFacilities,
  );
  const isUsingShippingAddressForBilling = useSelector(
    getIsUsingShippingAddressForBilling,
  );
  const suggestedAddresses = useSelector(getSuggestedAddresses);

  const shippingAddressFormRef = useRef<HTMLFormElement>(null);
  const billingAddressFormRef = useRef<HTMLFormElement>(null);
  const paymentDetailsFormRef = useRef<HTMLFormElement>(null);
  const paymentMethodFromRef = useRef<HTMLFormElement>(null);
  const shippingOptionsRef = useRef<HTMLFormElement>(null);
  const shippingMethodFormRef = useRef<HTMLFormElement>(null);
  const billingOptionsRef = useRef<HTMLFormElement>(null);
  const [billingFormKey, setBillingFormKey] = useState(`billing-${Date.now()}`);
  const [shippingFormKey, setShippingFormKey] = useState(
    `shipping-${Date.now()}`,
  );
  const [selectedSupplierFacilityId, setSelectedSupplierFacilityId] =
    useState(supplierFacilityId);

  useEffect(() => {
    setShippingFormKey(`shipping-${Date.now()}`);
  }, []);

  useEffect(() => {
    setBillingFormKey(`billing-${Date.now()}`);
  }, [billingAddress]);

  const handleEditClicked = (step) => {
    setStep({ step: step === selectedStep ? null : step });
  };

  /**
   * these are bad but I couldn't get a better way to make it work
   */
  const submitShippingAddress = () => {
    shippingAddressFormRef.current?.submit();
  };

  const submitShippingOption = () => {
    shippingOptionsRef.current?.submit();
  };

  const submitBillingAddress = () => {
    billingAddressFormRef.current?.submit();
  };

  const submitBillingOption = () => {
    billingOptionsRef.current?.submit();
  };

  const submitPaymentDetails = () => {
    paymentDetailsFormRef.current?.submit();
  };

  const submitPaymentMethod = () => {
    paymentMethodFromRef.current?.submit();
  };

  const submitShippingMethod = () => {
    shippingMethodFormRef.current?.submit();
  };
  /****************************************************************/

  return (
    <>
      {isAddressValidationModalOpen && (
        <AddressValidationModal
          originalAddress={originalAddress}
          suggestedAddresses={suggestedAddresses}
          cancelAddressValidation={closeAddressValidationModal}
          confirmAddress={(address) => confirmAddress(address)}
          editAddress={(address) => editAddress(address)}
        />
      )}
      <Content>
        <Step>
          <StepHeader>
            <StepTitle>Payment Method</StepTitle>
            <EditButton
              hidden={
                (paymentMethod === undefined &&
                  !visitedSteps[CUSTOMER_INFO_STEPS.PAYMENT_METHOD]) ||
                selectedStep === CUSTOMER_INFO_STEPS.PAYMENT_METHOD
              }
              onClick={() =>
                handleEditClicked(CUSTOMER_INFO_STEPS.PAYMENT_METHOD)
              }
            >
              Edit
            </EditButton>
          </StepHeader>
          <StepBody
            hidden={
              selectedStep !== CUSTOMER_INFO_STEPS.PAYMENT_METHOD &&
              paymentMethod === undefined
            }
          >
            {selectedStep === CUSTOMER_INFO_STEPS.PAYMENT_METHOD ? (
              <PaymentMethodForm ref={paymentMethodFromRef} />
            ) : (
              <PaymentMethod>
                Payment Method:{' '}
                {paymentMethod === PAYMENT_METHOD.CREDIT_CARD
                  ? 'Credit Card'
                  : paymentMethod === PAYMENT_METHOD.PURCHASE_ORDER
                  ? 'Purchase Order'
                  : 'Not Provided'}
              </PaymentMethod>
            )}
          </StepBody>
          {selectedStep === CUSTOMER_INFO_STEPS.PAYMENT_METHOD && (
            <StepFooter rightAlignContent={true}>
              <Button variant="primary" onClick={submitPaymentMethod}>
                Save
              </Button>
            </StepFooter>
          )}
        </Step>
        {maxSupplierFacilities > 1 && !isQuoteReadOnly && (
          <Step>
            <StepHeader>
              <StepTitle>Send From Facility</StepTitle>
              <EditButton
                hidden={
                  !visitedSteps[CUSTOMER_INFO_STEPS.SUPPLIER_FACILITY] ||
                  selectedStep === CUSTOMER_INFO_STEPS.SUPPLIER_FACILITY
                }
                onClick={() => {
                  handleEditClicked(CUSTOMER_INFO_STEPS.SUPPLIER_FACILITY);
                }}
              >
                Edit
              </EditButton>
            </StepHeader>
            <StepBody
              hidden={!visitedSteps[CUSTOMER_INFO_STEPS.SUPPLIER_FACILITY]}
            >
              {selectedStep === CUSTOMER_INFO_STEPS.SUPPLIER_FACILITY ? (
                <SupplierFacilitySelector
                  value={selectedSupplierFacilityId}
                  onChange={(selectedSupplierFacilityId) => {
                    setSelectedSupplierFacilityId(selectedSupplierFacilityId);
                  }}
                />
              ) : (
                <SupplierFacility>
                  Facility: {supplierFacilities.get(selectedSupplierFacilityId)}
                </SupplierFacility>
              )}
            </StepBody>
            {selectedStep === CUSTOMER_INFO_STEPS.SUPPLIER_FACILITY && (
              <StepFooter>
                <NavigationButtons>
                  <CancelButton
                    onClick={() => {
                      setSelectedSupplierFacilityId(supplierFacilityId);
                      setStep({ step: null });
                    }}
                  >
                    Cancel
                  </CancelButton>
                  <Button
                    variant="primary"
                    onClick={() => {
                      changeSupplierFacilityId({
                        supplierFacilityId: selectedSupplierFacilityId,
                      });
                    }}
                  >
                    Save
                  </Button>
                </NavigationButtons>
              </StepFooter>
            )}
          </Step>
        )}
        <Step>
          <StepHeader>
            <StepTitle>Ship To Facility</StepTitle>
            <EditButton
              hidden={
                (!facility && !visitedSteps[CUSTOMER_INFO_STEPS.FACILITY]) ||
                selectedStep === CUSTOMER_INFO_STEPS.FACILITY
              }
              onClick={() => {
                if (
                  shippingInformationType ==
                  SHIPPING_INFO_TYPES.ONE_TIME_ADDRESS
                )
                  showShippingAddressForm();
                handleEditClicked(CUSTOMER_INFO_STEPS.FACILITY);
              }}
            >
              Edit
            </EditButton>
          </StepHeader>
          <StepBody
            hidden={!facility && !visitedSteps[CUSTOMER_INFO_STEPS.FACILITY]}
          >
            {selectedStep === CUSTOMER_INFO_STEPS.FACILITY ? (
              isShowingShippingAddressForm ? (
                <CustomerAddressForm
                  initialAddress={
                    shippingInformation
                      ? shippingInformation
                      : !account
                      ? convertContactToAddressInformation(contact)
                      : undefined
                  }
                  key={shippingFormKey}
                  ref={shippingAddressFormRef}
                />
              ) : (
                <FacilitySelector
                  facility={account ? facility : undefined}
                  facilities={facilities}
                  ref={shippingOptionsRef}
                  setFunction={changeFacility}
                />
              )
            ) : (
              <ShippingInformationDisplay
                facility={facility}
                shippingInformation={shippingInformation}
                type={shippingInformationType}
              />
            )}
          </StepBody>
          {selectedStep === CUSTOMER_INFO_STEPS.FACILITY && (
            <StepFooter>
              {!isShowingShippingAddressForm ? (
                <>
                  {account && !contact.deletedAt && (
                    <AddAddressButton
                      onClick={() =>
                        openFacility({
                          accountId: account.id,
                          callBack: ({ facilityId }) =>
                            selectNewFacility(facilityId),
                          mode: FACILITY_DRAWER_MODES.CREATE,
                        })
                      }
                    >
                      Add New Facility
                    </AddAddressButton>
                  )}
                  <AddAddressButton onClick={showShippingAddressForm}>
                    Use One Time Address
                  </AddAddressButton>
                </>
              ) : (
                <AddAddressButton onClick={hideShippingAddressForm}>
                  Select Facility
                </AddAddressButton>
              )}
              <NavigationButtons>
                <CancelButton
                  onClick={() => {
                    setStep({ step: null });
                    hideShippingAddressForm();
                  }}
                >
                  Cancel
                </CancelButton>
                <Button
                  variant="primary"
                  onClick={() => {
                    isShowingShippingAddressForm
                      ? submitShippingAddress()
                      : submitShippingOption();
                  }}
                  isLoading={isAddressValidating}
                >
                  Save
                </Button>
              </NavigationButtons>
            </StepFooter>
          )}
        </Step>
        <Step>
          <StepHeader>
            <StepTitle>Bill To</StepTitle>
            {(selectedStep === CUSTOMER_INFO_STEPS.BILLING_ADDRESS ||
              isUsingShippingAddressForBilling) && (
              <AntCheckbox
                data-testid="use-shipping-address"
                disabled={!shippingInformation && !facility}
                checked={isUsingShippingAddressForBilling}
                onChange={(e) =>
                  changeUseShippingAddress({
                    useShippingAddress: e.target.checked,
                  })
                }
              >
                Use Shipping Address
              </AntCheckbox>
            )}
            <EditButton
              hidden={
                (!billingAddress &&
                  !visitedSteps[CUSTOMER_INFO_STEPS.BILLING_ADDRESS]) ||
                selectedStep === CUSTOMER_INFO_STEPS.BILLING_ADDRESS ||
                isUsingShippingAddressForBilling
              }
              onClick={() => {
                if (
                  billingInformationType == BILLING_INFO_TYPES.ONE_TIME_ADDRESS
                )
                  showBillingAddressForm();
                handleEditClicked(CUSTOMER_INFO_STEPS.BILLING_ADDRESS);
              }}
            >
              Edit
            </EditButton>
          </StepHeader>
          <StepBody
            hidden={
              !billingAddress &&
              !visitedSteps[CUSTOMER_INFO_STEPS.BILLING_ADDRESS]
            }
          >
            {selectedStep === CUSTOMER_INFO_STEPS.BILLING_ADDRESS &&
            !isUsingShippingAddressForBilling ? (
              isShowingBillingAddressForm ? (
                <CustomerAddressForm
                  initialAddress={
                    billingInformationType ==
                      BILLING_INFO_TYPES.ONE_TIME_ADDRESS && billingInformation
                      ? billingInformation
                      : convertContactToAddressInformation(contact)
                  }
                  key={billingFormKey}
                  ref={billingAddressFormRef}
                />
              ) : (
                <BillingAddressSelector
                  billingAddress={billingAddress}
                  billingAddresses={billingAddresses}
                  ref={billingOptionsRef}
                  setFunction={changeBillingAddress}
                />
              )
            ) : (
              <BillingInformationDisplay
                billingAddress={billingAddress}
                billingInformation={billingInformation}
                type={billingInformationType}
              />
            )}
          </StepBody>
          {selectedStep === CUSTOMER_INFO_STEPS.BILLING_ADDRESS && (
            <StepFooter>
              {!isShowingBillingAddressForm ? (
                <>
                  {account && !contact.deletedAt && (
                    <AddAddressButton
                      onClick={() =>
                        openBillingAddress({
                          accountId: account.id,
                          callBack: ({ billingAddressId }) =>
                            selectNewBillingAddress(billingAddressId),
                          mode: BILLING_ADDRESS_DRAWER_MODES.CREATE,
                        })
                      }
                    >
                      Add New Address
                    </AddAddressButton>
                  )}
                  <AddAddressButton onClick={showBillingAddressForm}>
                    Use One Time Address
                  </AddAddressButton>
                </>
              ) : (
                <>
                  {billingAddresses.length > 0 && (
                    <AddAddressButton onClick={hideBillingAddressForm}>
                      Select Existing Address
                    </AddAddressButton>
                  )}
                  {account && (
                    <AddAddressButton
                      onClick={() =>
                        openBillingAddress({
                          accountId: account.id,
                          callBack: ({ billingAddressId }) =>
                            selectNewBillingAddress(billingAddressId),
                          mode: BILLING_ADDRESS_DRAWER_MODES.CREATE,
                        })
                      }
                    >
                      Add New Address
                    </AddAddressButton>
                  )}
                  {!account && <div />}
                </>
              )}
              <NavigationButtons>
                {!(
                  paymentMethod == PAYMENT_METHOD.CREDIT_CARD &&
                  !billingInformation &&
                  !billingAddress
                ) && (
                  <CancelButton
                    onClick={() => {
                      setStep({ step: null });
                      if (
                        !(
                          paymentMethod == PAYMENT_METHOD.CREDIT_CARD &&
                          billingAddresses.length == 0
                        )
                      ) {
                        hideBillingAddressForm();
                      }
                    }}
                  >
                    Cancel
                  </CancelButton>
                )}
                <Button
                  variant="primary"
                  onClick={() => {
                    if (!isUsingShippingAddressForBilling) {
                      isShowingBillingAddressForm
                        ? submitBillingAddress()
                        : submitBillingOption();
                    } else {
                      setStep({
                        step: CUSTOMER_INFO_STEPS.SHIPPING_OPTIONS_AND_PRICING_SUMMARY,
                      });
                    }
                  }}
                >
                  Save
                </Button>
              </NavigationButtons>
            </StepFooter>
          )}
        </Step>
        <Step>
          <StepHeader>
            <StepTitle>Shipping Options and Pricing Summary</StepTitle>
            <EditButton
              hidden={
                (!shippingType &&
                  !visitedSteps[
                    CUSTOMER_INFO_STEPS.SHIPPING_OPTIONS_AND_PRICING_SUMMARY
                  ]) ||
                selectedStep ===
                  CUSTOMER_INFO_STEPS.SHIPPING_OPTIONS_AND_PRICING_SUMMARY
              }
              onClick={() =>
                handleEditClicked(
                  CUSTOMER_INFO_STEPS.SHIPPING_OPTIONS_AND_PRICING_SUMMARY,
                )
              }
            >
              Edit
            </EditButton>
          </StepHeader>
          <StepBody
            hidden={
              selectedStep !==
                CUSTOMER_INFO_STEPS.SHIPPING_OPTIONS_AND_PRICING_SUMMARY &&
              !hasValidShipping
            }
          >
            {selectedStep ===
            CUSTOMER_INFO_STEPS.SHIPPING_OPTIONS_AND_PRICING_SUMMARY ? (
              <ShippingOptionsForm ref={shippingMethodFormRef} quote={quote} />
            ) : (
              <ShippingOptionsDisplay quote={quote} />
            )}
          </StepBody>
          {selectedStep ===
            CUSTOMER_INFO_STEPS.SHIPPING_OPTIONS_AND_PRICING_SUMMARY && (
            <StepFooter>
              <div />
              <NavigationButtons>
                <CancelButton onClick={() => setStep({ step: null })}>
                  Cancel
                </CancelButton>
                <Button
                  variant="primary"
                  isDisabled={isFetchingShippingRates}
                  onClick={() => submitShippingMethod()}
                >
                  Save
                </Button>
              </NavigationButtons>
            </StepFooter>
          )}
        </Step>
        <Step>
          <StepHeader>
            <StepTitle>Payment Details</StepTitle>
            <EditButton
              hidden={
                !visitedSteps[CUSTOMER_INFO_STEPS.PAYMENT_DETAILS] ||
                selectedStep === CUSTOMER_INFO_STEPS.PAYMENT_DETAILS
              }
              onClick={() =>
                handleEditClicked(CUSTOMER_INFO_STEPS.PAYMENT_DETAILS)
              }
            >
              Edit
            </EditButton>
          </StepHeader>
          <StepBody
            hidden={
              selectedStep ===
                CUSTOMER_INFO_STEPS.SHIPPING_OPTIONS_AND_PRICING_SUMMARY ||
              !visitedSteps[CUSTOMER_INFO_STEPS.PAYMENT_DETAILS]
            }
          >
            <Elements stripe={stripePromise}>
              {selectedStep === CUSTOMER_INFO_STEPS.PAYMENT_DETAILS ? (
                <PaymentDetails ref={paymentDetailsFormRef} />
              ) : (
                <PaymentDetailsDisplay>
                  {paymentMethod
                    ? paymentMethod === PAYMENT_METHOD.CREDIT_CARD && card
                      ? `${card.brand} *${card.last4}`
                      : paymentMethod === PAYMENT_METHOD.PURCHASE_ORDER
                      ? `PO *${purchaseOrderNumber || ''}`
                      : ''
                    : 'Not Provided'}
                </PaymentDetailsDisplay>
              )}
            </Elements>
          </StepBody>
          {selectedStep === CUSTOMER_INFO_STEPS.PAYMENT_DETAILS && (
            <StepFooter>
              <div />
              <NavigationButtons>
                <CancelButton onClick={() => setStep({ step: null })}>
                  Cancel
                </CancelButton>
                <Button
                  variant="primary"
                  isDisabled={isValidatingCC}
                  isLoading={isValidatingCC}
                  onClick={submitPaymentDetails}
                >
                  Save
                </Button>
              </NavigationButtons>
            </StepFooter>
          )}
        </Step>
      </Content>
      <FacilityDrawer />
      <BillingAddressDrawer />
    </>
  );
};

export default CustomerInformation;
